import { Button, Icon } from "@nexus/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import {
  buildPersonaScriptShareUrl,
  formatPersonaScriptForCopy,
} from "@/features/persona-opportunities/lib/persona-script-clipboard";

export interface PersonaScriptActionsFooterProps {
  script: PersonaScriptDto;
  showAddThreeHooks: boolean;
  isSuggesting: boolean;
  onAddThreeHooks: () => void;
  onAiEdit: () => void;
  onSendToVendor: () => void;
  isAiEditing: boolean;
}

const LOADING_BUTTON_CLASS =
  "ring-1 ring-[var(--ai)] disabled:opacity-80 [&_svg]:animate-spin";
/** Figma: 36×36 outlined squares, 16px glyphs — do not use size="icon" (28px). */
const ICON_BUTTON_CLASS =
  "h-9 w-9 min-h-9 min-w-9 p-0 has-[>svg]:p-0 [&_svg]:size-4";
/** Figma: teal 1px border + faint mint fill, same 36px height as the row. */
const AI_BUTTON_CLASS =
  "h-9 border border-[var(--ai-border)] bg-[var(--ai-fill-light)] text-[var(--ai-text)] hover:bg-[var(--ai-fill-medium)]";
const ROW_BUTTON_CLASS = "h-9";

function PaperPlaneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-4 shrink-0"
      data-icon="send"
    >
      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
      <path d="m21.854 2.147-10.94 10.939" />
    </svg>
  );
}

function LoadingStatus({ children }: { children: string }) {
  return (
    <p
      role="status"
      className="flex items-center gap-2 rounded-md bg-[var(--ai-fill-light)] px-3 py-2 text-xs text-[var(--ai-text)]"
    >
      <Icon name="loader" color="inherit" size="sm" className="animate-spin" />
      {children}
    </p>
  );
}

async function writeClipboard(text: string): Promise<boolean> {
  const copied = navigator.clipboard?.writeText(text);
  if (!copied) return false;
  try {
    await copied;
    return true;
  } catch {
    return false;
  }
}

export function PersonaScriptActionsFooter({
  script,
  showAddThreeHooks,
  isSuggesting,
  onAddThreeHooks,
  onAiEdit,
  onSendToVendor,
  isAiEditing,
}: PersonaScriptActionsFooterProps) {
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = buildPersonaScriptShareUrl(script.id, window.location.origin);
    window.open(url, "_blank", "noopener,noreferrer");
    if (!(await writeClipboard(url))) {
      toast.error(
        t(
          "personaOpportunities.scripts.actions.shareError",
          "Couldn't copy the share link. Please try again.",
        ),
      );
      return;
    }
    setLinkCopied(true);
    toast.success(
      t(
        "personaOpportunities.scripts.actions.shareToast",
        "Preview opened — link copied to clipboard",
      ),
    );
  };

  const handleCopy = async () => {
    const untitled = t("personaOpportunities.scripts.untitledHook", {
      defaultValue: "Script {{slot}}",
      slot: script.slotIndex,
    });
    if (!(await writeClipboard(formatPersonaScriptForCopy(script, untitled)))) {
      toast.error(
        t(
          "personaOpportunities.scripts.actions.copyError",
          "Couldn't copy the script. Please try again.",
        ),
      );
      return;
    }
    setCopied(true);
    toast.success(
      t(
        "personaOpportunities.scripts.actions.copyToast",
        "Script copied to clipboard",
      ),
    );
  };

  return (
    <div className="flex flex-col gap-2" data-testid="persona-script-actions-footer">
      {isSuggesting || isAiEditing ? (
        <div className="flex flex-col gap-1" aria-live="polite">
          {isSuggesting ? (
            <LoadingStatus>
              {t(
                "personaOpportunities.scripts.hookVariations.writing",
                "Writing 3 alternative hooks...",
              )}
            </LoadingStatus>
          ) : null}
          {isAiEditing ? (
            <LoadingStatus>
              {t(
                "personaOpportunities.scripts.actions.aiEditing",
                "AI editing this script...",
              )}
            </LoadingStatus>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              className={ICON_BUTTON_CLASS}
              iconStart={linkCopied ? "check" : "share"}
              onClick={() => void handleShare()}
              aria-label={
                linkCopied
                  ? t("personaOpportunities.scripts.actions.shareCopied", "Link copied!")
                  : t("personaOpportunities.scripts.actions.share", "Share")
              }
            />
            <Button
              type="button"
              variant="secondary"
              className={ICON_BUTTON_CLASS}
              iconStart={copied ? "check" : "copy"}
              onClick={() => void handleCopy()}
              aria-label={
                copied
                  ? t("personaOpportunities.scripts.actions.copied", "Copied!")
                  : t("personaOpportunities.scripts.actions.copy", "Copy Script")
              }
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ai"
              iconStart={isAiEditing ? "loader" : "sparkles"}
              disabled={isAiEditing}
              aria-busy={isAiEditing}
              onClick={onAiEdit}
              className={isAiEditing ? `${AI_BUTTON_CLASS} ${LOADING_BUTTON_CLASS}` : AI_BUTTON_CLASS}
            >
              {t("personaOpportunities.scripts.actions.aiEdit", "AI Edit")}
            </Button>
            {showAddThreeHooks ? (
              <Button
                type="button"
                variant="ai"
                iconStart={isSuggesting ? "loader" : "plus"}
                disabled={isSuggesting}
                aria-busy={isSuggesting}
                onClick={onAddThreeHooks}
                className={isSuggesting ? `${AI_BUTTON_CLASS} ${LOADING_BUTTON_CLASS}` : AI_BUTTON_CLASS}
              >
                {t(
                  "personaOpportunities.scripts.hookVariations.addThree",
                  "Add 3 hooks",
                )}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            className={ROW_BUTTON_CLASS}
            onClick={onSendToVendor}
          >
            <PaperPlaneIcon />
            {t(
              "personaOpportunities.scripts.actions.sendToVendor",
              "Send to vendor",
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
